import { useInput } from '@libs/hooks';
import { TTextInputData, TWidgetProps } from '@libs/types';
import { camelCaseToKebabCase, getFieldName } from '@libs/utils';
import React from 'react';
import { TInputOption } from '../../types/options';
import { InputWrapper } from '../UI/Wrapper/InputWrapper';

const getInputMode = (options: TInputOption): 'text' | 'numeric' | 'decimal' => {
    const inputModeMap: { [key: string]: 'text' | 'numeric' | 'decimal' } = {
        number: 'decimal',
        numericPassword: 'numeric',
    };

    const inputType = options.inputType;

    if (inputType) {
        return inputModeMap[inputType] || 'text';
    }

    return 'text';
};

type TTextInput = {
    inputRef?: React.MutableRefObject<HTMLInputElement | null>;
    autoComplete?: string;
} & TWidgetProps<TTextInputData, TInputOption>;

const InputText = ({ widget, inputRef, autoComplete = 'off' }: TTextInput) => {
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const name = getFieldName(options.autofill);
    const { value, isFocused, isFilled, isError, errorMessage, handleChange, handleBlur, handleFocus, isDisabled, isHidden } = useInput(widget);
    const isPassword = options.inputType === 'numericPassword' || options.inputType === 'password';

    const inputAutoComplete = camelCaseToKebabCase(isPassword ? 'off' : (options.autofill ?? autoComplete));
    const inputType = isPassword ? 'password' : 'text';

    // This function temporarily sets the input field to readonly to prevent the password manager from triggering.
    // When the input field is focused, we remove the readonly attribute.
    const handleFocusWithReadOnly = (e: React.FocusEvent<HTMLInputElement>) => {
        if (handleFocus) {
            handleFocus(e);
        }
    };

    return (
        <InputWrapper
            dataTest={widget.uid}
            widget={widget}
            isDisabled={isDisabled}
            isHidden={isHidden}
            labelText={data.label}
            isFocused={isFocused}
            isError={isError}
            errorMessage={errorMessage}
            helperText={data.helperText}
            isFilled={isFilled}
            marginTop={options.margin?.top}
            marginRight={options.margin?.right}
            marginBottom={options.margin?.bottom}
            marginLeft={options.margin?.left}
        >
            <input
                ref={inputRef}
                value={value || ''}
                autoComplete={inputAutoComplete}
                name={name}
                autoFocus={options.onFocus || false}
                onFocus={handleFocusWithReadOnly}
                onBlur={handleBlur}
                readOnly={isDisabled}
                onChange={(e) => handleChange?.(e)}
                type={inputType}
                inputMode={getInputMode(options)}
                maxLength={data.submitLength || options.maxLength}
                required={data.mandatory}
                tabIndex={data.tabIndex}
            />
        </InputWrapper>
    );
};

export { InputText };
